import type { ReleasePlan, ReleasePlanDay, ReleasePost } from '../../../../../../redux/api/fymAPI.types';
import { addDays } from '../planMode';
import { planPostState, type PlanPostState } from '../planModel';

/**
 * The pure rules of the rollout run sheet (design `docs/superpowers/design-reference/music24-rollout-plan/`,
 * frames a1, b1, e1; owner decisions 2026-10-09). One unit is one post on one channel.
 */

/** The state of one channel pill of a step (design rp.css `.cp`). */
export type StepChannelState = 'notPlanned' | PlanPostState;

export interface StepChannelView {
  platform: string;
  format: string;
  /** ISO: the post time, else the slot of the channel. */
  at: string;
  state: StepChannelState;
  post: ReleasePost | null;
}

/** The node and the card tone of a step (rp.css `.stp.ok`, `.sch`, `.bad`, `.miss`). */
export type StepTone = 'ok' | 'sch' | 'bad' | 'miss' | 'plain';

export type StepPhase = 'build' | 'release' | 'after';

export interface PlanStepView {
  day: ReleasePlanDay;
  key: string;
  /**
   * The calendar day of the step: the day of the answer (fym-shared `scheduleRolloutSteps`). A step
   * whose planned day had passed moved forward (`moved`).
   */
  date: string;
  /** The planned day had passed, so the step moved forward (the "Moved" chip). */
  moved: boolean;
  phase: StepPhase;
  channels: StepChannelView[];
  tone: StepTone;
  /** The day is before today. */
  past: boolean;
  /** The asset of the step: ready, made now, missing, or none (a text step). */
  assetState: 'ready' | 'working' | 'missing' | 'none';
}

export function stepPhaseOf(offsetDays: number): StepPhase {
  if (offsetDays < 0) {
    return 'build';
  }
  return offsetDays === 0 ? 'release' : 'after';
}

function channelViews(day: ReleasePlanDay): StepChannelView[] {
  const channels =
    day.channels ?? day.platforms.map((platform) => ({ platform, format: day.format, at: day.date, postUuid: null }));
  return channels.map((channel) => {
    const post = day.posts.find((one) => one.platform === channel.platform) ?? null;
    return {
      platform: channel.platform,
      format: channel.format,
      at: post?.scheduledAt ?? channel.at,
      state: post === null ? 'notPlanned' : planPostState(post.status),
      post,
    };
  });
}

function toneOf(channels: StepChannelView[], assetState: PlanStepView['assetState']): StepTone {
  const states = channels.map((channel) => channel.state);
  if (states.includes('failed')) {
    return 'bad';
  }
  if (states.length > 0 && states.every((state) => state === 'posted')) {
    return 'ok';
  }
  if (states.some((state) => state === 'scheduled' || state === 'needsYou' || state === 'publishing')) {
    return 'sch';
  }
  if (states.every((state) => state === 'notPlanned') && assetState !== 'ready' && assetState !== 'none') {
    return 'miss';
  }
  return 'plain';
}

/**
 * The steps of a release plan, in the table order. The Canvas mark is a milestone, not a step. The
 * date of a step is the date of the answer: the server moves a step whose day had passed into the
 * time that is left (fym-shared `scheduleRolloutSteps`), and `offsetDays` is the real offset of
 * that day. A step of an AI reels plan counts from the start day of the plan.
 */
export function planSteps(plan: ReleasePlan, today: string): PlanStepView[] {
  return plan.days
    .filter((day) => !day.isTask)
    .map((day) => {
      const date = day.date.slice(0, 10);
      const channels = channelViews(day);
      const assetState = day.asset === null ? 'none' : day.asset.status;
      return {
        day,
        key: day.key,
        date,
        moved: plan.kind !== 'reels' && day.movedToToday,
        phase: stepPhaseOf(day.offsetDays),
        channels,
        tone: toneOf(channels, assetState),
        past: date < today,
        assetState,
      };
    });
}

export interface PlanCounts {
  /** Every unit of the plan (a skipped step leaves the count). */
  total: number;
  posted: number;
  scheduled: number;
  drafts: number;
  failed: number;
  /** Units of a step whose asset is missing or made now, with no post. */
  needAsset: number;
  /** Units with no post whose asset is ready: "Plan all N posts" adds them. */
  readyToPlan: number;
}

/** The summary counts (design a1, b1): one unit = one post on one channel. */
export function planCounts(steps: PlanStepView[]): PlanCounts {
  const counts: PlanCounts = { total: 0, posted: 0, scheduled: 0, drafts: 0, failed: 0, needAsset: 0, readyToPlan: 0 };
  steps
    .filter((step) => step.day.skipped !== true)
    .forEach((step) => {
      step.channels.forEach((channel) => {
        counts.total += 1;
        switch (channel.state) {
          case 'posted':
            counts.posted += 1;
            break;
          case 'scheduled':
          case 'needsYou':
          case 'publishing':
            counts.scheduled += 1;
            break;
          case 'draft':
            counts.drafts += 1;
            break;
          case 'failed':
            counts.failed += 1;
            break;
          case 'notPlanned':
            if (step.assetState === 'ready') {
              counts.readyToPlan += 1;
            } else {
              counts.needAsset += 1;
            }
        }
      });
    });
  return counts;
}

/**
 * The steps that have no post yet, by the state of their asset (QA 2026-10-28, imp-2). With no
 * channel a step has no unit, so `planCounts` gives 0 for it: the summary then counts the steps.
 */
export function unplannedStepCounts(steps: PlanStepView[]): { ready: number; missing: number } {
  const counts = { ready: 0, missing: 0 };
  steps
    .filter((step) => step.day.skipped !== true)
    .filter((step) => step.channels.every((channel) => channel.state === 'notPlanned'))
    .forEach((step) => {
      if (step.assetState === 'ready') {
        counts.ready += 1;
      } else if (step.assetState === 'missing' || step.assetState === 'working') {
        counts.missing += 1;
      }
    });
  return counts;
}

/** The share (0..100) of each part of the summary bar; the parts add up to 100. */
export function barShares(counts: PlanCounts): Array<{ part: 'ok' | 'sch' | 'dr' | 'bad' | 'miss'; share: number }> {
  if (counts.total === 0) {
    return [];
  }
  const parts: Array<['ok' | 'sch' | 'dr' | 'bad' | 'miss', number]> = [
    ['ok', counts.posted],
    ['sch', counts.scheduled],
    ['dr', counts.drafts + counts.readyToPlan],
    ['bad', counts.failed],
    ['miss', counts.needAsset],
  ];
  return parts
    .filter(([, value]) => value > 0)
    .map(([part, value]) => ({ part, share: Math.round((value / counts.total) * 1000) / 10 }));
}

/** The place of an offset on the rail (0..100), from the first to the last day of the table. */
export function railPosition(offset: number, first: number, last: number): number {
  if (last === first) {
    return 0;
  }
  return Math.round(((offset - first) / (last - first)) * 10_000) / 100;
}

/** The smallest gap between two rail labels, in percent of the rail width (QA finding F13). */
export const RAIL_LABEL_MIN_GAP = 7;

/**
 * The rail labels that do not collide (QA finding F13: "Nov 12", "Nov 13" and "Nov 14" printed on
 * top of each other near release day). Release day keeps its label first, then the first and the
 * last day, then the other days in time order; a label nearer than `minGap` percent to a kept label
 * is dropped. Its point stays on the rail. Answers the kept offsets in time order. Pure.
 */
export function railLabelsThatFit(
  offsets: readonly number[],
  first: number,
  last: number,
  minGap: number = RAIL_LABEL_MIN_GAP
): number[] {
  const unique = [...new Set(offsets)].sort((a, b) => a - b);
  const priority = [
    ...unique.filter((offset) => offset === 0),
    ...unique.filter((offset) => offset !== 0 && (offset === first || offset === last)),
    ...unique.filter((offset) => offset !== 0 && offset !== first && offset !== last),
  ];
  const kept: number[] = [];
  priority.forEach((offset) => {
    const at = railPosition(offset, first, last);
    if (kept.every((other) => Math.abs(railPosition(other, first, last) - at) >= minGap)) {
      kept.push(offset);
    }
  });
  return kept.sort((a, b) => a - b);
}

/** The days from `from` to `to` (`YYYY-MM-DD`), negative when `to` is before `from`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The next post that goes out (scheduled, after now), for the summary of a running plan (b1). */
export function nextScheduledPost(
  steps: PlanStepView[],
  now: Date
): { step: PlanStepView; channel: StepChannelView } | null {
  const candidates = steps
    .flatMap((step) => step.channels.map((channel) => ({ step, channel })))
    .filter(({ channel }) => channel.state === 'scheduled' && Date.parse(channel.at) >= now.getTime())
    .sort((a, b) => Date.parse(a.channel.at) - Date.parse(b.channel.at));
  return candidates[0] ?? null;
}

// ---------------------------------------------------------------- Free plan fit (frame e1)

export interface DraftFit {
  postUuid: string;
  /** `YYYY-MM` of the post time (UTC: the quota months are UTC months). */
  month: string;
  fits: boolean;
  /** The time is past: the server refuses it (`M24_POST_TIME_PAST`). */
  past: boolean;
}

export interface FitResult {
  drafts: DraftFit[];
  /** The months the drafts fall in: the units used, the limit, what the plan needs, what fits. */
  months: Array<{ month: string; used: number; limit: number; needs: number; fits: number }>;
}

/**
 * "Schedule all" on a plan with a monthly limit (owner decision 8): the drafts in time order take
 * the units left in their month (`limit − used` this month, the whole limit in a later month: a
 * post pays in the month it is scheduled). A draft that does not fit stays a draft. With no limit
 * every draft fits. A draft whose time is past never fits.
 */
export function fitDrafts(input: {
  drafts: ReadonlyArray<{ uuid: string; scheduledAt: string }>;
  limit: number | null;
  used: number;
  now: Date;
}): FitResult {
  const thisMonth = input.now.toISOString().slice(0, 7);
  const left = new Map<string, number>();
  const months = new Map<string, { month: string; used: number; limit: number; needs: number; fits: number }>();
  const ordered = [...input.drafts].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  const drafts = ordered.map((draft) => {
    const month = draft.scheduledAt.slice(0, 7);
    const past = Date.parse(draft.scheduledAt) < input.now.getTime();
    if (input.limit === null) {
      return { postUuid: draft.uuid, month, fits: !past, past };
    }
    const used = month === thisMonth ? input.used : 0;
    if (!left.has(month)) {
      left.set(month, Math.max(0, input.limit - used));
      months.set(month, { month, used, limit: input.limit, needs: 0, fits: 0 });
    }
    const entry = months.get(month) as { needs: number; fits: number };
    entry.needs += 1;
    const remaining = left.get(month) ?? 0;
    const fits = !past && remaining > 0;
    if (fits) {
      left.set(month, remaining - 1);
      entry.fits += 1;
    }
    return { postUuid: draft.uuid, month, fits, past };
  });
  return { drafts, months: [...months.values()] };
}

/** Every draft post of the plan (not skipped steps). */
export function planDrafts(steps: PlanStepView[]): ReleasePost[] {
  return steps
    .filter((step) => step.day.skipped !== true)
    .flatMap((step) => step.channels)
    .filter((channel) => channel.state === 'draft' && channel.post !== null)
    .map((channel) => channel.post as ReleasePost);
}

/** The platform of each post of the plan, by post uuid (the launch summary splits them by channel). */
export function platformOfPlanPosts(steps: readonly PlanStepView[]): Map<string, string> {
  const platforms = new Map<string, string>();
  steps.forEach((step) =>
    step.channels.forEach((channel) => {
      if (channel.post !== null) {
        platforms.set(channel.post.uuid, channel.platform);
      }
    })
  );
  return platforms;
}

// ---------------------------------------------------------------- Needs you (frame b1)

export type NeedKind = 'failed' | 'draft' | 'missing' | 'manualPost' | 'canvas';

export interface PlanNeed {
  kind: NeedKind;
  step: PlanStepView | null;
  channel: StepChannelView | null;
  /** ISO or `YYYY-MM-DD`: the order of the list (oldest first). */
  when: string;
}

const DAY_MS = 86_400_000;

/**
 * The "Needs you" list (frame b1 note): a failed post; a "needs you" manual post; a draft whose
 * time is in the next 7 days; a missing asset whose step is in the next 10 days; the open Canvas
 * mark from 3 days before release day. Oldest first.
 */
export function planNeeds(input: {
  steps: PlanStepView[];
  now: Date;
  today: string;
  releaseDate: string | null;
  canvasOpen: boolean;
}): PlanNeed[] {
  const needs: PlanNeed[] = [];
  const in7Days = input.now.getTime() + 7 * DAY_MS;
  input.steps
    .filter((step) => step.day.skipped !== true)
    .forEach((step) => {
      step.channels.forEach((channel) => {
        if (channel.state === 'failed') {
          needs.push({ kind: 'failed', step, channel, when: channel.at });
        } else if (channel.state === 'needsYou') {
          needs.push({ kind: 'manualPost', step, channel, when: channel.at });
        } else if (channel.state === 'draft' && Date.parse(channel.at) <= in7Days) {
          needs.push({ kind: 'draft', step, channel, when: channel.at });
        }
      });
      const unplanned = step.channels.every((channel) => channel.state === 'notPlanned');
      if (
        step.assetState === 'missing' &&
        unplanned &&
        step.date >= input.today &&
        daysBetween(input.today, step.date) <= 10
      ) {
        needs.push({ kind: 'missing', step, channel: null, when: step.date });
      }
    });
  if (input.canvasOpen && input.releaseDate !== null && daysBetween(input.today, input.releaseDate) <= 3) {
    needs.push({ kind: 'canvas', step: null, channel: null, when: addDays(input.releaseDate, 1) });
  }
  return needs.sort((a, b) => a.when.localeCompare(b.when));
}
